import { useState, useEffect } from 'react';
import { Loader2, Clock, XCircle, AlertTriangle, Play, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Kv({ label, children }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="text-sm font-medium">{children}</span>
    </div>
  );
}

function StatusBadge({ report }) {
  if (!report) return <Badge variant="secondary" className="text-xs">no runs</Badge>;
  if (report.circuit_breaker_tripped) {
    return <Badge variant="destructive" className="text-xs">BREAKER TRIPPED</Badge>;
  }
  if (report.error) {
    return <Badge variant="destructive" className="text-xs">error</Badge>;
  }
  if (report.high_errors > 0) {
    return <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">partial errors</Badge>;
  }
  if (report.high_applied === 0 && report.high_already_applied === 0) {
    return <Badge variant="secondary" className="text-xs">no-op</Badge>;
  }
  return <Badge variant="default" className="text-xs bg-green-600">success</Badge>;
}

export function AfpvAutoReconPanel() {
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const fetchLastRun = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.afpvAutoReconLastRun();
      setLastRun(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLastRun();
  }, []);

  const runNow = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await diagnosticsAPI.afpvAutoReconRunNow();
      setRunResult(data);
      toast.success(
        data.circuit_breaker_tripped
          ? 'Circuit breaker tripped, nothing applied'
          : `Applied ${data.high_applied} HIGH matches`
      );
      // Refresh the last-run display.
      fetchLastRun();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const report = runResult || lastRun?.last_run?.report;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          AFPV Auto-Reconciliation Cron
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Daily at 09:00 UTC. Applies HIGH-confidence 1:1 matches automatically.
          LOW matches (Booking.com amount-date) stay on the recon card for human review.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Last run summary */}
        <div className="flex items-center gap-3 flex-wrap">
          <Kv label="Last run">
            {loading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : lastRun?.last_run?.created_at
                ? new Date(lastRun.last_run.created_at).toLocaleString()
                : 'never'
            }
          </Kv>
          <StatusBadge report={report} />
          <div className="flex-1" />
          <Button onClick={fetchLastRun} disabled={loading} size="sm" variant="outline">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={runNow} disabled={running} size="sm">
            {running
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Play className="h-4 w-4 mr-1" />
            }
            Run Now
          </Button>
        </div>

        {/* Circuit breaker alert */}
        {report?.circuit_breaker_tripped && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-800 text-sm">Circuit Breaker Tripped</div>
              <div className="text-sm text-red-700">{report.circuit_breaker_reason}</div>
              <div className="text-xs text-red-600 mt-1">
                Nothing was applied. Investigate upstream data before re-running.
              </div>
            </div>
          </div>
        )}

        {/* Error alert */}
        {report?.error && !report?.circuit_breaker_tripped && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-2">
            <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-800 text-sm">Error</div>
              <div className="text-sm text-red-700">{report.error}</div>
            </div>
          </div>
        )}

        {/* Stats grid */}
        {report && !report.error && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-md border p-2 text-center">
              <div className="text-2xl font-bold text-green-600">{report.high_applied ?? 0}</div>
              <div className="text-xs text-muted-foreground">HIGH applied</div>
            </div>
            <div className="rounded-md border p-2 text-center">
              <div className="text-2xl font-bold text-muted-foreground">{report.high_already_applied ?? 0}</div>
              <div className="text-xs text-muted-foreground">already applied (skip)</div>
            </div>
            <div className="rounded-md border p-2 text-center">
              <div className={`text-2xl font-bold ${(report.high_errors ?? 0) > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                {report.high_errors ?? 0}
              </div>
              <div className="text-xs text-muted-foreground">HIGH errors</div>
            </div>
            <div className="rounded-md border p-2 text-center">
              <div className="text-2xl font-bold text-amber-600">{report.low_left_for_review ?? 0}</div>
              <div className="text-xs text-muted-foreground">LOW for review</div>
            </div>
          </div>
        )}

        {/* LOW review link */}
        {report && (report.low_left_for_review ?? 0) > 0 && (
          <div className="text-sm text-muted-foreground">
            {report.low_left_for_review} LOW-confidence matches are waiting for human review on the{' '}
            <span className="font-medium text-foreground">Pennylane Reconciliation Preview</span> card above.
          </div>
        )}

        {/* Applied details table */}
        {report?.applied_details?.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Applied details</div>
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="py-1.5 px-2 text-left text-xs font-medium">Txn ID</th>
                    <th className="py-1.5 px-2 text-left text-xs font-medium">Invoice ID</th>
                    <th className="py-1.5 px-2 text-left text-xs font-medium">Status</th>
                    <th className="py-1.5 px-2 text-left text-xs font-medium">Action ID</th>
                  </tr>
                </thead>
                <tbody>
                  {report.applied_details.slice(0, 50).map((d, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-1 px-2 font-mono text-xs">{d.transaction_id}</td>
                      <td className="py-1 px-2 font-mono text-xs">{d.invoice_id}</td>
                      <td className="py-1 px-2">
                        {d.status === 'applied' && <Badge variant="default" className="text-xs bg-green-600">applied</Badge>}
                        {d.status === 'already_applied' && <Badge variant="secondary" className="text-xs">already</Badge>}
                        {d.status === 'apply_partial' && <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">partial</Badge>}
                        {d.status === 'exception' && <Badge variant="destructive" className="text-xs">error</Badge>}
                        {!['applied', 'already_applied', 'apply_partial', 'exception'].includes(d.status) && (
                          <Badge variant="secondary" className="text-xs">{d.status}</Badge>
                        )}
                      </td>
                      <td className="py-1 px-2 font-mono text-xs truncate max-w-[200px]">
                        {d.action_id || d.error || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {report.applied_details.length > 50 && (
                <div className="text-xs text-muted-foreground p-2">
                  ... and {report.applied_details.length - 50} more
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timing info */}
        {report && (
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <Kv label="Trigger">{report.trigger ?? '?'}</Kv>
            <Kv label="Started">{report.started_at ? new Date(report.started_at).toLocaleString() : '?'}</Kv>
            <Kv label="Finished">{report.finished_at ? new Date(report.finished_at).toLocaleString() : '?'}</Kv>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
